import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faSearch,
  faList,
  faFileImport,
  faProcedures,
  faStethoscope,
  faCircleDot
} from '@fortawesome/free-solid-svg-icons';
import { fetchPracticePatients } from '../../../store/slices/practiceSlice';
import { deletePatient } from '../../../store/slices/patientSlice';
import socketService from '../../../services/socketService';
import { toast } from 'react-toastify';
import practiceService from '../../../services/practiceService';
import Table from '../../../components/Common/Table/Table';
import StatusBadge from '../../../components/Common/StatusBadge/StatusBadge';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import SearchBar from '../../../components/Common/SearchBar/SearchBar';
import '../AdminManagement/AdminManagement.css';
import './PatientManagement.css';

/** `patients.id` when present — aligns list actions with PatientEditView and PUT /patients/:id. */
function resolvePatientRecordId(patient) {
  if (!patient) return null;
  // Prefer patient_table_id (patients.id), fallback to id (which is COALESCE(p.id, u.id) in backend)
  const pid = patient.patient_table_id ?? patient.id;
  console.debug('[PatientManagement] Resolved Patient ID:', pid, 'from object:', patient);
  return pid;
}

const PatientManagement = ({ practiceId, isReadOnly = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => state.auth || {});
  const {
    practicePatients = [],
    listLoading = {},
    error,
    pagination,
    activeContextPracticeId
  } = useSelector((state) => state.practice ?? {});
  const patientsLoading = listLoading.patients;
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [localStatusFilter, setLocalStatusFilter] = useState(null);
  const [initialTableLoading, setInitialTableLoading] = useState(true);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [dashboardPatients, setDashboardPatients] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardPatientsLoaded, setDashboardPatientsLoaded] = useState(false);
  const [activeDashboardTable, setActiveDashboardTable] = useState(null);
  const [dashboardCurrentPage, setDashboardCurrentPage] = useState(1);
  const [dashboardEntriesPerPage, setDashboardEntriesPerPage] = useState(10);
  const hideStatsCards = user?.role_id === 1 || user?.role_id === 2;
  const initialLoadTrackerRef = useRef({ practiceId: null, started: false });

  const listFetchParamsRef = useRef({
    currentPage: 1,
    entriesPerPage: 30,
    searchTerm: '',
    status: null,
    sortBy: 'name',
    sortOrder: 'asc'
  });
  useEffect(() => {
    listFetchParamsRef.current = {
      currentPage,
      entriesPerPage,
      searchTerm,
      status: localStatusFilter,
      sortBy,
      sortOrder
    };
  }, [currentPage, entriesPerPage, searchTerm, localStatusFilter, sortBy, sortOrder]);

  // Normalize any backend status representation to numeric IDs used by filters:
  // Active=2, Pending=3, Locked=4.
  const getNormalizedStatusId = (patient) => {
    const raw = patient?.status ?? patient?.status_id ?? patient?.patient_status;
    if (raw === null || raw === undefined || raw === '' || raw === 0 || raw === '0') return 2;
    if (typeof raw === 'number') {
      if (raw === 3) return 3;
      if (raw === 4) return 4;
      return 2;
    }
    const txt = String(raw).trim().toLowerCase();
    if (txt === 'pending' || txt === '3') return 3;
    if (txt === 'locked' || txt === '4') return 4;
    if (txt === 'active' || txt === '2' || txt === '1' || txt === '0') return 2;
    return 2;
  };

  // Get status order for sorting (Active=1, Pending=2, Locked=3, Unknown=99)
  const getStatusOrder = (patient) => {
    const normalized = getNormalizedStatusId(patient);
    if (normalized === 3) return 2;
    if (normalized === 4) return 3;
    return 1;
  };

  const getPatientSortValue = (patient, key) => {
    if (!patient) return '';
    switch (key) {
      case 'idx':
        return Number(resolvePatientRecordId(patient) || patient.id || 0);
      case 'status':
        return getStatusOrder(patient);
      case 'vitals':
        return String(patient.vitals || '').toLowerCase();
      case 'name':
        return `${String(patient.last_name || '').trim()}, ${String(patient.first_name || '').trim()}`
          .replace(/^,\s*|\s*,$/g, '')
          .toLowerCase();
      case 'home':
        return String(patient.phone_number || patient.phone || '').toLowerCase();
      case 'cell':
        return String(patient.cell_phone_number || '').toLowerCase();
      case 'rpm':
        return patient.rpm_start_date ? new Date(patient.rpm_start_date).getTime() : 0;
      case 'readings':
        return Number(patient.readings_count || patient.number_of_readings || patient.total_readings || 0);
      case 'svc':
        return Number(patient.service_time || 0);
      case 'prov':
        return String(patient.provider_name || patient.provider || `Provider ${patient.provider_id || ''}`).toLowerCase();
      case 'cg':
        return String(
          Array.isArray(patient.caregiver_names) && patient.caregiver_names.length > 0
            ? patient.caregiver_names.join(', ')
            : patient.caregiver_name || patient.caregiver || ''
        ).toLowerCase();
      default:
        return String(patient[key] || '').toLowerCase();
    }
  };

  const normalizeVitalsForDisplay = (rawVitals) => {
    const raw = String(rawVitals || '').toUpperCase();
    const hasBP = raw.includes('BP') || raw.includes('BLOOD PRESSURE');
    const hasBG = raw.includes('BG') || raw.includes('BLOOD GLUCOSE') || raw.includes('GLUCOSE');
    const hasW = raw.includes('WT') || raw.includes(' WEIGHT') || raw.includes(' W ') || raw.endsWith(' W') || raw.startsWith('W ') || raw === 'W';
    const ordered = [];
    if (hasBP) ordered.push('BP');
    if (hasBG) ordered.push('BG');
    if (hasW) ordered.push('W');
    return ordered;
  };

  // Debounce search term for real-time filtering
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        setSearchTerm(localSearchTerm);
        setCurrentPage(1);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [localSearchTerm, searchTerm]);

  useEffect(() => {
    if (practiceId) {
      if (initialLoadTrackerRef.current.practiceId !== practiceId) {
        initialLoadTrackerRef.current = { practiceId, started: false };
        setInitialTableLoading(true);
      }
      // On initial screen load or practice switch, invalidate cache to guarantee fresh readings and service time
      if (!initialLoadTrackerRef.current.started) {
        practiceService.invalidatePracticePatientsCache(practiceId);
      }
      const params = {
        page: currentPage,
        limit: entriesPerPage === 'all' ? 1000 : parseInt(entriesPerPage),
        search: searchTerm,
        status: localStatusFilter, // Pass status filter to server
        sortBy,
        sortOrder
      };
      initialLoadTrackerRef.current.started = true;
      dispatch(fetchPracticePatients({ id: practiceId, params }));
    }
  }, [practiceId, dispatch, currentPage, entriesPerPage, searchTerm, localStatusFilter, sortBy, sortOrder]);

  useEffect(() => {
    if (
      initialTableLoading &&
      initialLoadTrackerRef.current.started &&
      !patientsLoading
    ) {
      setInitialTableLoading(false);
    }
  }, [patientsLoading, initialTableLoading]);

  useEffect(() => {
    if (!Array.isArray(practicePatients)) {
      setFilteredPatients([]);
      return;
    }

    let filtered = [...practicePatients];

    // Apply status filter if provided (2=Active, 3=Pending, 4=Locked)
    if (localStatusFilter) {
      const statusId = Number(localStatusFilter);
      filtered = filtered.filter((patient) => getNormalizedStatusId(patient) === statusId);
    }

    // Apply client-side search filter for real-time results
    if (localSearchTerm.trim()) {
      filtered = filtered.filter(patient => {
        const fullName = `${patient.first_name || ''} ${patient.last_name || ''}`.toLowerCase();
        const email = patient.email || '';
        const phone = patient.phone || patient.home_phone || '';

        return fullName.includes(localSearchTerm.toLowerCase()) ||
          email.toLowerCase().includes(localSearchTerm.toLowerCase()) ||
          phone.toLowerCase().includes(localSearchTerm.toLowerCase());
      });
    }

    // Default status sort + user-selected column sorting.
    filtered = filtered.sort((a, b) => {
      // Always keep status grouping fixed: Active -> Pending -> Locked.
      const statusA = getPatientSortValue(a, 'status');
      const statusB = getPatientSortValue(b, 'status');
      if (statusA !== statusB) {
        return statusA - statusB;
      }

      const valueA = getPatientSortValue(a, sortBy);
      const valueB = getPatientSortValue(b, sortBy);

      if (sortBy === 'name') {
        const nameA = String(valueA || '');
        const nameB = String(valueB || '');
        const compareResult = nameA.localeCompare(nameB, undefined, {
          numeric: true,
          sensitivity: 'base',
          ignorePunctuation: true
        });
        if (compareResult !== 0) {
          return sortOrder === 'asc' ? compareResult : -compareResult;
        }
        // deterministic tie-breaker
        const idA = Number(resolvePatientRecordId(a) || a.id || 0);
        const idB = Number(resolvePatientRecordId(b) || b.id || 0);
        return sortOrder === 'asc' ? idA - idB : idB - idA;
      }

      if (sortBy === 'status') {
        // Within same status (A/P/L), always sort by name alphabetically.
        const nameA = getPatientSortValue(a, 'name');
        const nameB = getPatientSortValue(b, 'name');
        const nameCompare = String(nameA).localeCompare(String(nameB), undefined, {
          numeric: true,
          sensitivity: 'base',
          ignorePunctuation: true
        });
        if (nameCompare !== 0) {
          return nameCompare;
        }

        // Deterministic final tie-breaker.
        const idA = Number(resolvePatientRecordId(a) || a.id || 0);
        const idB = Number(resolvePatientRecordId(b) || b.id || 0);
        return idA - idB;
      }

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const compareResult = String(valueA).localeCompare(String(valueB), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
      return sortOrder === 'asc' ? compareResult : -compareResult;
    });

    setFilteredPatients(filtered);
  }, [practicePatients, localSearchTerm, localStatusFilter, sortBy, sortOrder]);

  useEffect(() => {
    setDashboardPatients([]);
    setDashboardStats(null);
    setDashboardPatientsLoaded(false);
    setDashboardLoading(false);
  }, [practiceId]);

  const loadDashboardStats = async () => {
    if (!practiceId || hideStatsCards) return;
    try {
      const response = await practiceService.getPracticePatients(practiceId, {
        statsOnly: '1',
        includeDashboardStats: '1'
      });
      const payload = response?.data?.data || {};
      setDashboardStats(payload.dashboard_stats || null);
    } catch (err) {
      console.error('[PatientManagement] loadDashboardStats failed:', err);
      setDashboardStats(null);
    }
  };

  const loadDashboardPatients = async (force = false) => {
    if (!practiceId || hideStatsCards || dashboardLoading) return;
    if (!force && dashboardPatientsLoaded) return;
    try {
      setDashboardLoading(true);
      const response = await practiceService.getPracticePatients(practiceId, {
        page: 1,
        limit: 10000,
        includeUploadFlags: '1',
        includeDashboardEnrichment: '1'
      });
      const payload = response?.data?.data || {};
      const rows = payload.patients || [];
      setDashboardPatients(Array.isArray(rows) ? rows : []);
      if (payload.dashboard_stats) {
        setDashboardStats(payload.dashboard_stats);
      }
      setDashboardPatientsLoaded(true);
    } catch (err) {
      console.error('[PatientManagement] loadDashboardPatients failed:', err);
      setDashboardPatients([]);
      setDashboardPatientsLoaded(true);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Dashboard tile counts only (provider / caregiver / system caregiver) — not on initial patient list.
  useEffect(() => {
    if (!practiceId || hideStatsCards) return;
    loadDashboardStats();
  }, [practiceId, hideStatsCards]);

  // Scroll to dashboard table when activated
  useEffect(() => {
    if (activeDashboardTable) {
      const el = document.getElementById('dashboard-detail-table');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [activeDashboardTable]);

  // Reset dashboard pagination when category changes
  useEffect(() => {
    setDashboardCurrentPage(1);
  }, [activeDashboardTable]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(key);
    setSortOrder('asc');
  };

  // Handler for status filter badges
  const handleStatusFilter = (statusId) => {
    // If clicking the same filter, toggle it off
    if (localStatusFilter === statusId) {
      setLocalStatusFilter(null);
    } else {
      setLocalStatusFilter(statusId);
    }
    setCurrentPage(1);
  };

  const handleDashboardCardClick = (key) => {
    loadDashboardPatients(true);
    setActiveDashboardTable(key);
  };

  const handleTotalPatientCardClick = () => {
    setActiveDashboardTable(null);
    setLocalStatusFilter(null);
    setLocalSearchTerm('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Socket.IO real-time updates and focus/event listeners for patient list
  useEffect(() => {
    if (!practiceId) return;

    socketService.connect();
    socketService.joinPracticeRoom(practiceId);

    const handlePatientVitalUpdate = () => {
      const { currentPage: page, entriesPerPage: ep, searchTerm: st, status, sortBy: sb, sortOrder: so } = listFetchParamsRef.current;
      const params = {
        page,
        limit: ep === 'all' ? 1000 : parseInt(ep, 10),
        search: st,
        status,
        sortBy: sb,
        sortOrder: so
      };
      // Invalidate cached list so # of readings/service time update after new measurements or follow-ups.
      practiceService.invalidatePracticePatientsCache(practiceId);
      dispatch(fetchPracticePatients({ id: practiceId, params }));
      loadDashboardStats();
      if (activeDashboardTable) {
        loadDashboardPatients(true);
      }
    };

    socketService.onPatientVitalUpdate(handlePatientVitalUpdate);

    const handleWindowFocus = () => {
      handlePatientVitalUpdate();
    };

    const handleDataChanged = (event) => {
      const evtPracticeId = event?.detail?.practiceId;
      if (evtPracticeId && String(evtPracticeId) !== String(practiceId)) return;
      handlePatientVitalUpdate();
    };

    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('patient:measurement-changed', handleDataChanged);
    window.addEventListener('patient:followup-changed', handleDataChanged);

    return () => {
      socketService.offPatientVitalUpdate();
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('patient:measurement-changed', handleDataChanged);
      window.removeEventListener('patient:followup-changed', handleDataChanged);
    };
  }, [practiceId, dispatch, activeDashboardTable]);

  // Use server-side pagination data
  const totalPages = pagination.total_pages || 1;
  const currentPatients = filteredPatients;
  const practiceContextMismatch =
    activeContextPracticeId != null &&
    String(activeContextPracticeId) !== String(practiceId);
  const tablePatients = practiceContextMismatch ? [] : currentPatients;
  const showNoPatientsMessage =
    !patientsLoading &&
    !initialTableLoading &&
    currentPatients.length === 0;

  // Keep list page valid after filters/search shrink the server result set.
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handleAddPatient = () => {
    navigate(`/practices/${practiceId}/add-patient`);
  };

  const patientStatusForBadge = (patient) => {
    const normalized = getNormalizedStatusId(patient);
    if (normalized === 3) return 'Pending';
    if (normalized === 4) return 'Locked';
    return 'Active';
  };

  const handleViewPatient = (patient) => {
    const pid = resolvePatientRecordId(patient);
    if (pid == null) {
      console.warn('[PatientManagement] Cannot navigate: patient ID is null', patient);
      toast.error('Unable to open patient details: Missing ID');
      return;
    }

    if (!practiceId) {
      console.warn('[PatientManagement] Cannot navigate: practiceId is missing');
      toast.error('Unable to open patient details: Practice contextual missing');
      return;
    }

    console.info(`[PatientManagement] Navigating to detail: /practices/${practiceId}/patients/${pid}`);
    navigate(`/practices/${practiceId}/patients/${pid}`);
  };

  /** List “edit” icon → AddPatient single-page form (`?editId=`), not the tabbed PatientEditView. */
  const handleEditPatient = (patient) => {
    const pid = resolvePatientRecordId(patient);
    if (pid == null) return;
    navigate(`/practices/${practiceId}/add-patient?editId=${pid}`);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [patientToDelete, setPatientToDelete] = useState(null);

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (patientToDelete) {
      const patientName = `${patientToDelete.first_name || ''} ${patientToDelete.last_name || ''}`.trim();
      const pid = resolvePatientRecordId(patientToDelete);

      if (pid == null) {
        toast.error('Cannot delete: missing patient id');
        setShowDeleteModal(false);
        setPatientToDelete(null);
        return;
      }

      try {
        await dispatch(deletePatient({ practiceId, patientId: pid })).unwrap();

        // Close modal immediately for better UX
        setShowDeleteModal(false);
        setPatientToDelete(null);
        toast.success(`Patient ${patientName} deleted successfully`);

        // Refresh patient list in background
        practiceService.invalidatePracticePatientsCache(practiceId);
        dispatch(
          fetchPracticePatients({
            id: practiceId,
            params: {
              page: pagination?.current_page || 1,
              limit: pagination?.per_page || 10,
              search: searchTerm,
              status: localStatusFilter
            }
          })
        );
      } catch (error) {
        // Even on error, we should close the modal if the error implies it's gone
        setShowDeleteModal(false);
        setPatientToDelete(null);

        console.error('Error deleting patient:', error);
        const errorMessage = typeof error === 'string' ? error : (error.message || 'Failed to delete patient');
        toast.error(errorMessage);

        // Refresh list to stay in sync
        practiceService.invalidatePracticePatientsCache(practiceId);
        dispatch(
          fetchPracticePatients({
            id: practiceId,
            params: {
              page: pagination?.current_page || 1,
              limit: pagination?.per_page || 10,
              search: searchTerm,
              status: localStatusFilter
            }
          })
        );
      }
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPatientToDelete(null);
  };


  // REMOVED: Partial loading state to prevent jarring UI jumps
  // We now use an inline loading indicator in the search bar and table

  const pageNum = pagination?.current_page || currentPage || 1;
  const perPage = pagination?.per_page || (entriesPerPage === 'all' ? 1000 : parseInt(entriesPerPage, 10)) || 10;

  const patientColumns = useMemo(
    () => [
      {
        key: 'idx',
        title: '#',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (_, i) => <span>{(pageNum - 1) * perPage + i + 1}</span>
      },
      {
        key: 'status',
        title: 'Status',
        sortable: true,
        className: 'reusable-table-centered-col',
        render: (p) => <StatusBadge status={patientStatusForBadge(p)} />
      },
      {
        key: 'vitals',
        title: 'Vitals',
        sortable: true,
        render: (p) => {
          const vitals = normalizeVitalsForDisplay(p.vitals);

          return vitals.length > 0 ? (
            vitals.map((v, idx) => (
              <span key={idx} className="patient-management-vitals-badge">
                {v}
              </span>
            ))
          ) : (
            <span className="patient-management-vitals-badge" style={{ opacity: 0.5 }}>
              -
            </span>
          );
        }
      },
      {
        key: 'name',
        title: 'Name',
        sortable: true,
        render: (p) => renderPatientNameLink(p)
      },
      { key: 'home', title: 'Home Phone', sortable: true, render: (p) => p.phone_number || p.phone || 'N/A' },
      { key: 'cell', title: 'Cell Phone', sortable: true, render: (p) => p.cell_phone_number || 'N/A' },
      {
        key: 'rpm',
        title: 'RPM Start Date',
        sortable: true,
        render: (p) =>
          p.rpm_start_date
            ? new Date(p.rpm_start_date).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric'
            })
            : 'N/A'
      },
      {
        key: 'readings',
        title: '# of Reading',
        sortable: true,
        render: (p) => {
          const count = Number(p.readings_count || p.number_of_readings || p.total_readings || 0);
          return Number.isFinite(count) ? String(count) : '0';
        }
      },
      {
        key: 'svc',
        title: 'Service Time',
        sortable: true,
        render: (p) => {
          const mins = Number(p.service_time || 0);
          return Number.isFinite(mins) ? String(mins) : '0';
        }
      },
      // Provider and Caregiver columns removed from table as requested
      // {
      //   key: 'prov',
      //   title: 'Provider',
      //   sortable: true,
      //   render: (p) => p.provider_name || `Provider ${p.provider_id || 'N/A'}`
      // },
      // {
      //   key: 'cg',
      //   title: 'Caregiver',
      //   sortable: true,
      //   render: (p) => {
      //     const names = p.caregiver_names || (p.caregiver_name && p.caregiver_name !== 'Caregiver N/A' ? [p.caregiver_name] : []);
      //     if (names.length === 0) {
      //       return (
      //         <span className="patient-management-caregiver-badge" style={{ opacity: 0.5, backgroundColor: '#f5f5f5', color: '#666', border: '1px solid #ddd' }}>
      //           N/A
      //         </span>
      //       );
      //     }
      //     return (
      //       <div className="patient-management-caregiver-list">
      //         {names.map((name, idx) => (
      //           <span key={idx} className="patient-management-caregiver-badge">
      //             {name}
      //           </span>
      //         ))}
      //       </div>
      //     );
      //   }
      // },
      {
        key: 'act',
        title: 'Action',
        className: 'reusable-table-centered-col',
        render: (p) => (
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <ActionButtons
              onView={() => handleViewPatient(p)}
              onEdit={!isReadOnly ? () => handleEditPatient(p) : null}
              onDelete={!isReadOnly ? () => handleDeletePatient(p) : null}
            />
          </div>
        )
      }
    ],
    [pageNum, perPage, practiceId, navigate, dispatch, searchTerm, isReadOnly]
  );

  const getPatientDisplayName = (p) =>
    `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*|\s*,$/g, '').trim() || 'N/A';

  const formatDisplayDate = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  const getPatientDob = (p) => {
    const formatted = p?.dob || formatDisplayDate(p?.date_of_birth);
    return formatted || 'N/A';
  };

  const getEnrolledDate = (p) => {
    const formatted =
      p?.enrolled_date ||
      formatDisplayDate(p?.created_at) ||
      formatDisplayDate(p?.rpm_start_date);
    return formatted || 'N/A';
  };

  const getMeasurementPeriod = (p) =>
    p?.measurement_period || dashboardStats?.billing_period?.label || 'N/A';

  const renderPatientNameLink = (p) => (
    <a
      href="#"
      className="admin-management-name-link"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleViewPatient(p);
      }}
    >
      {getPatientDisplayName(p)}
    </a>
  );

  const RECENT_UPLOAD_MS = 24 * 60 * 60 * 1000;

  const getRecentUploadDate = (p) =>
    p?.last_vital_upload_at || p?.last_upload_at || p?.upload_date || null;

  const isRecentUpload = (p) => {
    if (p?.has_recent_upload === true) return true;
    if (p?.has_recent_upload === false && p?.last_vital_upload_at) return false;
    const uploadDate = getRecentUploadDate(p);
    if (!uploadDate) return false;
    const d = new Date(uploadDate);
    if (Number.isNaN(d.getTime())) return false;
    return Date.now() - d.getTime() <= RECENT_UPLOAD_MS;
  };

  const isMissedUpload = (p) => {
    if (p?.has_missed_upload === true) return true;
    if (p?.has_missed_upload === false) return false;
    return !isRecentUpload(p);
  };

  const getHoursSinceLastUpload = (p) => {
    const uploadDate = getRecentUploadDate(p);
    if (!uploadDate) return null;
    const d = new Date(uploadDate);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60));
  };

  const isAbnormal = (p) => {
    if (Boolean(p?.has_abnormal_measurement) || Number(p?.abnormal_count || 0) > 0) return true;
    if (p?.abnormal_measurement_date || p?.abnormal_data_summary) return true;

    const summary = String(p?.abnormal_data_summary || p?.data_summary || '').trim();
    if (summary) {
      const bpMatch = summary.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/);
      if (bpMatch) {
        const sys = parseFloat(bpMatch[1]);
        const dia = parseFloat(bpMatch[2]);
        if (!isNaN(sys) && (sys >= 140 || sys < 100)) return true;
        if (!isNaN(dia) && (dia >= 90 || dia < 60)) return true;
      }
      const pulseMatch = summary.match(/\((\d+(?:\.\d+)?)\)/);
      if (pulseMatch) {
        const pulse = parseFloat(pulseMatch[1]);
        if (!isNaN(pulse) && (pulse > 100 || pulse < 60)) return true;
      }
      const bgMatch = summary.match(/^(\d+)(?:\s*\/\s*(\d+))?/);
      if (bgMatch && !summary.includes('lbs') && !summary.includes('BMI')) {
        const v1 = parseInt(bgMatch[1], 10);
        if (!isNaN(v1) && (v1 >= 180 || v1 <= 70)) return true;
      }
    }
    return false;
  };
  const readingCount = (p) => Number(p?.readings_count || p?.number_of_readings || p?.total_readings || 0);
  const serviceMinutes = (p) => Number(p?.service_time || 0);

  const normalizePatientDate = (raw) => {
    if (!raw || raw === 'N/A') return null;
    const iso = String(raw).trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const isEligible99453 = (p) => {
    if (p?.eligible_99453 === true) return true;
    if (p?.eligible_99453 === false) return false;
    const from = dashboardStats?.billing_period?.date_from;
    const to = dashboardStats?.billing_period?.date_to;
    if (!from || !to) return false;
    const enrolledDay = normalizePatientDate(p?.enrolled_date || p?.created_at);
    if (!enrolledDay) return false;
    return enrolledDay >= from && enrolledDay <= to;
  };

  const get99458UnitCount = (minutes) => {
    const mins = Number(minutes) || 0;
    if (mins < 40) return 0;
    if (mins < 60) return 1;
    return 2;
  };

  const dashboardBuckets = useMemo(() => {
    const list = Array.isArray(dashboardPatients) ? dashboardPatients : [];
    return {
      recentUploads: list.filter((p) => isRecentUpload(p)),
      missedUploads: list.filter((p) => isMissedUpload(p)),
      abnormalMeasurements: list.filter((p) => isAbnormal(p)),
      cpt99453: list.filter((p) => isEligible99453(p)),
      cpt99445: list.filter((p) => { const r = readingCount(p); return r >= 2 && r <= 15; }),
      cpt99454: list.filter((p) => readingCount(p) >= 16),
      cpt99470: list.filter((p) => { const m = serviceMinutes(p); return m >= 10 && m < 20; }),
      cpt99457: list.filter((p) => serviceMinutes(p) >= 20),
      cpt99458: list.filter((p) => get99458UnitCount(serviceMinutes(p)) > 0)
    };
  }, [dashboardPatients, dashboardStats]);

  const getDashboardCardCount = (key) => {
    if (dashboardStats) {
      const statMap = {
        recentUploads: dashboardStats.recent_uploads,
        missedUploads: dashboardStats.missed_uploads,
        abnormalMeasurements: dashboardStats.abnormal_measurements,
        cpt99453: dashboardStats.cpt99453,
        cpt99445: dashboardStats.cpt99445,
        cpt99454: dashboardStats.cpt99454,
        cpt99470: dashboardStats.cpt99470,
        cpt99457: dashboardStats.cpt99457,
        cpt99458: dashboardStats.cpt99458
      };
      if (statMap[key] !== undefined && statMap[key] !== null) {
        return Math.max(Number(statMap[key]), dashboardBuckets[key]?.length || 0);
      }
    }
    return dashboardBuckets[key]?.length ?? 0;
  };

  const dashboardTableMap = useMemo(() => {
    const baseCols = [
      { key: 'status', title: 'Status', render: (p) => <StatusBadge status={patientStatusForBadge(p)} /> },
      { key: 'name', title: 'Name', render: (p) => renderPatientNameLink(p) },
      { key: 'dob', title: 'DOB', render: (p) => getPatientDob(p) },
      { key: 'phone', title: 'Phone', render: (p) => p.phone_number || p.phone || p.cell_phone_number || 'N/A' },
      { key: 'rpm_start', title: 'RPM Start Date', render: (p) => p.rpm_start_date || 'N/A' }
    ];

    return {
      recentUploads: {
        title: 'Recent Uploads',
        rows: dashboardBuckets.recentUploads,
        columns: [
          ...baseCols,
          {
            key: 'measurement_date',
            title: 'Measurement Date',
            render: (p) => p.measurement_date || formatDisplayDate(getRecentUploadDate(p)) || 'N/A'
          },
          { key: 'measurement_period', title: 'Measurement Period', render: (p) => getMeasurementPeriod(p) },
          { key: 'data', title: 'Data', render: (p) => p.data_summary || p.vitals || 'N/A' },
          {
            key: 'upload_dt',
            title: 'Upload Date/Time',
            render: (p) => formatDisplayDate(getRecentUploadDate(p)) || getRecentUploadDate(p) || 'N/A'
          },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      missedUploads: {
        title: 'Missed Uploads',
        rows: dashboardBuckets.missedUploads,
        columns: [
          ...baseCols,
          { key: 'last_updated', title: 'Last Updated', render: (p) => p.updated_at || 'N/A' },
          {
            key: 'last_measurement',
            title: 'Last measurement date',
            render: (p) => p.last_measurement_date || formatDisplayDate(getRecentUploadDate(p)) || 'N/A'
          },
          {
            key: 'hours_missed',
            title: 'Hours since last upload',
            render: (p) => {
              const hrs = getHoursSinceLastUpload(p);
              return hrs == null ? 'Never' : String(hrs);
            }
          },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      abnormalMeasurements: {
        title: 'Abnormal Measurement',
        rows: dashboardBuckets.abnormalMeasurements,
        columns: [
          ...baseCols,
          {
            key: 'measurement_date',
            title: 'Measurement Date',
            render: (p) =>
              p.abnormal_measurement_date ||
              p.measurement_date ||
              p.last_measurement_date ||
              'N/A'
          },
          { key: 'measurement_period', title: 'Measurement Period', render: (p) => getMeasurementPeriod(p) },
          {
            key: 'data',
            title: 'Data',
            render: (p) => {
              const text = p.abnormal_data_summary || p.data_summary || p.vitals || 'N/A';
              const bpMatch = String(text).match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)(?:\s*\((.*?)\))?/);
              if (bpMatch) {
                const sys = parseFloat(bpMatch[1]);
                const dia = parseFloat(bpMatch[2]);
                const pulseStr = bpMatch[3];
                const pulse = pulseStr ? parseFloat(pulseStr) : null;
                const sysClass = sys >= 140 ? 'value-high' : sys < 100 ? 'value-low' : 'value-normal';
                const diaClass = dia >= 90 ? 'value-high' : dia < 60 ? 'value-low' : 'value-normal';
                const pulseClass = pulse != null ? (pulse > 100 ? 'value-high' : pulse < 60 ? 'value-low' : 'value-normal') : '';
                return (
                  <span style={{ fontWeight: 600 }}>
                    <span className={sysClass}>{bpMatch[1]}</span>
                    <span> / </span>
                    <span className={diaClass}>{bpMatch[2]}</span>
                    {pulseStr && (
                      <span className={pulseClass ? ` ${pulseClass}` : ''}> ({pulseStr})</span>
                    )}
                  </span>
                );
              }
              const bgMatch = String(text).match(/^(\d+(?:\.\d+)?)(?:\s*\/\s*(\d+(?:\.\d+)?))?/);
              if (bgMatch && !String(text).includes('lbs') && !String(text).includes('BMI')) {
                const v1 = parseFloat(bgMatch[1]);
                const v1Class = v1 >= 180 ? 'value-high' : v1 <= 70 ? 'value-low' : 'value-normal';
                return (
                  <span style={{ fontWeight: 600 }}>
                    <span className={v1Class}>{bgMatch[1]}</span>
                    {bgMatch[2] ? (
                      <>
                        <span> / </span>
                        <span className={parseFloat(bgMatch[2]) >= 180 ? 'value-high' : parseFloat(bgMatch[2]) <= 70 ? 'value-low' : 'value-normal'}>
                          {bgMatch[2]}
                        </span>
                      </>
                    ) : null}
                  </span>
                );
              }
              return text;
            }
          },
          {
            key: 'upload_dt',
            title: 'Upload date/time',
            render: (p) => formatDisplayDate(getRecentUploadDate(p)) || getRecentUploadDate(p) || 'N/A'
          },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99453: {
        title: '99453',
        rows: dashboardBuckets.cpt99453,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99445: {
        title: '99445',
        rows: dashboardBuckets.cpt99445,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => getEnrolledDate(p) },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99454: {
        title: '99454',
        rows: dashboardBuckets.cpt99454,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => getEnrolledDate(p) },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99470: {
        title: '99470',
        rows: dashboardBuckets.cpt99470,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => getEnrolledDate(p) },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99457: {
        title: '99457',
        rows: dashboardBuckets.cpt99457,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => getEnrolledDate(p) },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      },
      cpt99458: {
        title: '99458',
        rows: dashboardBuckets.cpt99458,
        columns: [
          ...baseCols.slice(0, 1),
          { key: 'account', title: 'Account', render: (p) => String(p.patient_id || p.patient_table_id || p.id || 'N/A') },
          ...baseCols.slice(1),
          { key: 'enrolled', title: 'Enrolled Date', render: (p) => getEnrolledDate(p) },
          { key: 'readings', title: '# of Reading', render: (p) => String(readingCount(p)) },
          { key: 'service', title: 'Service Time', render: (p) => String(serviceMinutes(p)) }
        ]
      }
    };
  }, [dashboardBuckets, dashboardStats, practiceId]);

  return (
    <div className="patient-management-container">
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      {/* Dashboard Stats Cards */}
      {!hideStatsCards && (
        <div className="patient-management-stats-container">
          {/* Total Patient Card */}
          <div
            className="patient-management-stat-card total-patient"
            role="button"
            tabIndex={0}
            onClick={handleTotalPatientCardClick}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleTotalPatientCardClick();
              }
            }}
          >
            <div className="patient-management-stat-header">
              <div className="patient-management-stat-icon-wrapper">
                <FontAwesomeIcon icon={faProcedures} className="patient-management-stat-icon" />
              </div>
              <h6 className="patient-management-stat-title">Total Patient</h6>
            </div>
            <span className="patient-management-stat-count">{pagination?.total || practicePatients?.length || 0}</span>
            <div className="patient-management-stat-badges">
              <div
                className={`patient-management-badge-box patient-management-badge-aa ${localStatusFilter === 2 ? 'active-filter' : ''} ${localStatusFilter && localStatusFilter !== 2 ? 'inactive-filter' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleStatusFilter(2); setActiveDashboardTable(null); }}
                title="Filter by Active"
              >
                A
              </div>
              <div
                className={`patient-management-badge-box patient-management-badge-p ${localStatusFilter === 3 ? 'active-filter' : ''} ${localStatusFilter && localStatusFilter !== 3 ? 'inactive-filter' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleStatusFilter(3); setActiveDashboardTable(null); }}
                title="Filter by Pending"
              >
                P
              </div>
              <div
                className={`patient-management-badge-box patient-management-badge-l ${localStatusFilter === 4 ? 'active-filter' : ''} ${localStatusFilter && localStatusFilter !== 4 ? 'inactive-filter' : ''}`}
                onClick={(e) => { e.stopPropagation(); handleStatusFilter(4); setActiveDashboardTable(null); }}
                title="Filter by Locked"
              >
                L
              </div>
            </div>
          </div>

          {/* Regular Stats Cards */}
          {[
            { key: 'recentUploads', title: 'Recent Uploads' },
            { key: 'missedUploads', title: 'Missed Uploads' }
          ].map((card) => (
            <div
              className="patient-management-stat-card"
              key={card.key}
              onClick={() => handleDashboardCardClick(card.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleDashboardCardClick(card.key)}
            >
              <div className="patient-management-stat-header">
                <div className="patient-management-stat-icon-wrapper">
                  <FontAwesomeIcon icon={faFileImport} className="patient-management-stat-icon" />
                </div>
                <h6 className="patient-management-stat-title">{card.title}</h6>
                {card.subtitle && (
                  <span className="patient-management-stat-subtitle">{card.subtitle}</span>
                )}
              </div>
              <span className="patient-management-stat-count">{getDashboardCardCount(card.key)}</span>
            </div>
          ))}

          {/* Abnormal Measurement */}
          <div
            className="patient-management-stat-card"
            onClick={() => handleDashboardCardClick('abnormalMeasurements')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleDashboardCardClick('abnormalMeasurements')}
          >
            <div className="patient-management-stat-header">
              <div className="patient-management-stat-icon-wrapper">
                <FontAwesomeIcon icon={faStethoscope} className="patient-management-stat-icon" />
              </div>
              <h6 className="patient-management-stat-title">Abnormal Measurement</h6>
            </div>
            <span className="patient-management-stat-count">
              {getDashboardCardCount('abnormalMeasurements')}
            </span>
          </div>

          {/* CPT Codes */}
          {[
            { code: '99453', key: 'cpt99453' },
            { code: '99445', key: 'cpt99445' },
            { code: '99454', key: 'cpt99454' },
            { code: '99470', key: 'cpt99470' },
            { code: '99457', key: 'cpt99457' },
            { code: '99458', key: 'cpt99458' }
          ].map((item) => (
            <div
              className="patient-management-stat-card cpt-card"
              key={item.code}
              onClick={() => handleDashboardCardClick(item.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleDashboardCardClick(item.key)}
            >
              <div className="patient-management-stat-header">
                <h6 className="patient-management-stat-title">{item.code}</h6>
              </div>
              <span className="patient-management-stat-count">{getDashboardCardCount(item.key)}</span>
            </div>
          ))}
        </div>
      )}

      {activeDashboardTable && dashboardTableMap[activeDashboardTable] && (
        <div id="dashboard-detail-table">
          <div style={{ display: 'flex', alignItems: 'center', margin: '30px 0 20px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
            <h6 style={{
              margin: '0 15px',
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#7f8c8d',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}>
              {dashboardTableMap[activeDashboardTable].title} Details
            </h6>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#ddd' }}></div>
          </div>
          <div className="patient-management-table-container mb-4">
            <Table
              columns={dashboardTableMap[activeDashboardTable].columns}
              data={(() => {
                const rows = dashboardTableMap[activeDashboardTable].rows || [];
                if (dashboardEntriesPerPage === 'all') return rows;
                const limit = parseInt(dashboardEntriesPerPage, 10);
                const startIndex = (dashboardCurrentPage - 1) * limit;
                return rows.slice(startIndex, startIndex + limit);
              })()}
              isLoading={dashboardLoading}
              loadingMode="spinner"
              emptyMessage="No patients found for this category"
              onRowClick={handleViewPatient}
              getRowKey={(p, i) => resolvePatientRecordId(p) ?? p.user_id ?? i}
            />
          </div>
          <ListScreenPaginationBar
            entriesValue={dashboardEntriesPerPage}
            onEntriesChange={(value) => {
              setDashboardEntriesPerPage(value);
              setDashboardCurrentPage(1);
            }}
            showPageSelector={dashboardEntriesPerPage !== 'all'}
            currentPage={dashboardCurrentPage}
            totalPages={Math.ceil((dashboardTableMap[activeDashboardTable].rows?.length || 0) / (dashboardEntriesPerPage === 'all' ? 999999 : parseInt(dashboardEntriesPerPage, 10))) || 1}
            onPageChange={setDashboardCurrentPage}
          />
        </div>
      )}

      {!activeDashboardTable && (
        <>
          <div className="admin-management-header-row">
            <h2 className="admin-management-title-text">Patient List</h2>

            <div className="admin-management-header-actions">
              <SearchBar
                value={localSearchTerm}
                onChange={setLocalSearchTerm}
                className="admin-management-search-wrapper"
              />
              {!isReadOnly && (
                <button type="button" className="list-screen-add-btn" onClick={handleAddPatient}>
                  <FontAwesomeIcon icon={faPlus} className="admin-management-add-icon" />
                  Add Patient
                </button>
              )}
            </div>
          </div>

          {showNoPatientsMessage && (
            <div className="patient-management-empty-state" role="status">
              <p>No patients found.</p>
              {(localSearchTerm.trim() || localStatusFilter) && (
                <button
                  type="button"
                  className="patient-management-clear-filters-btn"
                  onClick={() => {
                    setLocalSearchTerm('');
                    setSearchTerm('');
                    setLocalStatusFilter(null);
                    setCurrentPage(1);
                  }}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          <Table
            columns={patientColumns}
            data={tablePatients}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            isLoading={patientsLoading || initialTableLoading}
            loadingMode="spinner"
            emptyMessage="No patients found"
            onRowClick={handleViewPatient}
            getRowKey={(p, i) => resolvePatientRecordId(p) ?? p.user_id ?? i}
          />

          {/* Pagination */}
          <div className="list-screen-pagination-section">
            <div className="list-screen-pagination-left">
              <div className="list-screen-entries-selector">
                <span>Show</span>
                <select
                  className="list-screen-entries-select"
                  style={{ width: '80px' }}
                  value={entriesPerPage}
                  onChange={(e) => handleEntriesChange(e.target.value)}
                >
                  <option value="10">10</option>
                  <option value="30">30</option>
                  <option value="50">50</option>
                  <option value="all">All</option>
                </select>
                <span>entries</span>
              </div>
              <button
                type="button"
                className="list-screen-option-btn"
                onClick={() => navigate(`/event-logs/Patient/all?practiceId=${practiceId}`)}
              >
                <FontAwesomeIcon icon={faList} className="admin-management-add-icon" />
                Events Log
              </button>
            </div>

            <div className="list-screen-pagination-right">
              <div className="list-screen-page-selector">
                <span>Page</span>
                <select
                  className="list-screen-page-select"
                  style={{ width: '60px' }}
                  value={pagination.current_page || 1}
                  onChange={(e) => handlePageChange(parseInt(e.target.value, 10))}
                >
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <option key={page} value={page}>{page}</option>
                  ))}
                </select>
                <span>of {totalPages}</span>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button
                type="button"
                className="modal-close"
                onClick={cancelDelete}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to delete the patient{' '}
                <strong>{patientToDelete ? `${patientToDelete.first_name || ''} ${patientToDelete.last_name || ''}`.trim() : ''}</strong>?
              </p>
              <p className="modal-warning">
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={cancelDelete}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientManagement;